if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        
        // Skip initialization for custom galleries
        if (this.hasAttribute('data-custom-handled') || this.hasAttribute('data-no-auto-init')) {
          return;
        }
        
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }

        this.removeModalOpenersWithVideo(); // remove video modal openers at start
        this.initScrollAutoplay();

        window.addEventListener('resize', this.removeModalOpenersWithVideo.bind(this));
      }

      removeModalOpenersWithVideo() {
        this.elements.viewer.querySelectorAll('.product__media-item').forEach((mediaItem) => {
          const modalOpener = mediaItem.querySelector('.product__modal-opener');
          const deferredMedia = mediaItem.querySelector('deferred-media');
          const hasVideoContent = deferredMedia && (
            deferredMedia.querySelector('video') ||
            deferredMedia.querySelector('iframe[src*="youtube.com"]') ||
            deferredMedia.querySelector('iframe.js-vimeo')
          );
          if (modalOpener && hasVideoContent) {
            modalOpener.remove(); // completely remove modal opener for video
          }
        });
      }

      initScrollAutoplay() {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const mediaItem = entry.target;
            const isVisible = entry.isIntersecting;
            const deferredMedia = mediaItem.querySelector('deferred-media');
      
            if (!deferredMedia) return;
      
            if (isVisible) {
              // Clear existing injected media
              [...deferredMedia.children].forEach(child => {
                if (child.tagName !== 'TEMPLATE') child.remove();
              });
      
              const template = deferredMedia.querySelector('template');
              if (!template?.content?.firstElementChild) return;
      
              const clone = template.content.firstElementChild.cloneNode(true);
              deferredMedia.appendChild(clone);
      
              if (clone.tagName === 'VIDEO') {
                clone.muted = true;
                clone.currentTime = 0;
                clone.play().catch(() => {});
              } else if (clone.tagName === 'IFRAME') {
                const isVimeo = clone.classList.contains('js-vimeo');
                if (isVimeo && typeof Vimeo !== 'undefined') {
                  const player = new Vimeo.Player(clone);
                  player.setCurrentTime(0).then(() => player.play()).catch(() => {});
                } else {
                  const src = new URL(clone.src);
                  src.searchParams.set('autoplay', '1');
                  src.searchParams.set('mute', '1');
                  clone.src = src.toString();
                }
              }
            } else {
              // Pause when not visible
              const iframe = deferredMedia.querySelector('iframe.js-vimeo, iframe[src*="youtube.com"]');
              const video = deferredMedia.querySelector('video');
              if (video) video.pause();
              if (iframe && typeof Vimeo !== 'undefined' && iframe.classList.contains('js-vimeo')) {
                const player = new Vimeo.Player(iframe);
                player.pause().catch(() => {});
              }
            }
          });
        }, { threshold: 0.6 });
      
        this.elements.viewer.querySelectorAll('.product__media-item').forEach((mediaItem) => {
          observer.observe(mediaItem);
        });
      }


      onSlideChanged(event) {
        const currentMedia = event.detail.currentElement;
        const deferredMedia = currentMedia.querySelector('deferred-media');
        const iframe = currentMedia.querySelector('iframe.js-vimeo, iframe[src*="youtube.com"]');
        const video = currentMedia.querySelector('video');
      
        this.setActiveThumbnail(
          this.elements.thumbnails?.querySelector(`[data-target="${currentMedia.dataset.mediaId}"]`)
        );
      
        if (deferredMedia) {
          // deferredMedia.removeAttribute('loaded');
          // deferredMedia.loadContent(false);
      
          const checkInterval = setInterval(() => {
            const iframe = currentMedia.querySelector('iframe.js-vimeo, iframe[src*="youtube.com"]');
            const video = currentMedia.querySelector('video');
      
            if (video) {
              try {
                video.muted = true;
                video.currentTime = 0;
                video.play();
              } catch {}
              clearInterval(checkInterval);
            }
      
            if (iframe && typeof Vimeo !== 'undefined' && iframe.classList.contains('js-vimeo')) {
              const player = new Vimeo.Player(iframe);
              player.setCurrentTime(0).then(() => player.play()).catch(() => {});
              clearInterval(checkInterval);
            }
          }, 100);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach(el => el.classList.remove('is-active'));
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const rect = activeMedia.getBoundingClientRect();
          if (rect.top > -0.5) return;
          window.scrollTo({ top: rect.top + window.scrollY, behavior: 'smooth' });
        });

        this.playActiveMedia(activeMedia);
        this.removeModalOpenersWithVideo(); // re-check after switch

        const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        if (activeThumbnail) {
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails.querySelectorAll('button').forEach(btn => btn.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          if (window.accessibilityStrings?.imageAvailable) {
            this.elements.liveRegion.setAttribute('aria-hidden', false);
            this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
            setTimeout(() => this.elements.liveRegion.setAttribute('aria-hidden', true), 2000);
          }
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
      
        const deferredMedia = activeItem.querySelector('deferred-media');
        if (!deferredMedia) return;
      
        // Remove all non-template children
        [...deferredMedia.children].forEach(child => {
          if (child.tagName !== 'TEMPLATE') child.remove();
        });
      
        const template = deferredMedia.querySelector('template');
        if (!template?.content?.firstElementChild) return;
      
        const clone = template.content.firstElementChild.cloneNode(true);
        deferredMedia.appendChild(clone);
      
        // Autoplay logic
        if (clone.tagName === 'VIDEO') {
          clone.muted = true;
          clone.currentTime = 0;
          clone.play().catch(() => {});
        } else if (clone.tagName === 'IFRAME') {
          const isVimeo = clone.classList.contains('js-vimeo');
          if (isVimeo && typeof Vimeo !== 'undefined') {
            const player = new Vimeo.Player(clone);
            player.setCurrentTime(0).then(() => player.play()).catch(() => {});
          } else {
            const src = new URL(clone.src);
            src.searchParams.set('autoplay', '1');
            src.searchParams.set('mute', '1');
            clone.src = src.toString();
          }
        }
      }


      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (this.stickyHeader) {
          this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
        }
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}